/**
 * Input Manager - Handles keyboard, mouse, and gamepad input
 */

import { IDisposable, IUpdatable } from "../core/Engine.types";

export type KeyState = "pressed" | "held" | "released" | "up";

export interface InputState {
    keyboard: Map<string, KeyState>;
    mouse: {
        x: number;
        y: number;
        deltaX: number;
        deltaY: number;
        buttons: Map<number, KeyState>;
        wheel: number;
    };
    pointerLocked: boolean;
}

export class InputManager implements IUpdatable, IDisposable {
    private _canvas: HTMLCanvasElement;
    private _state: InputState;
    private _previousKeys: Set<string> = new Set();
    private _currentKeys: Set<string> = new Set();
    private _previousMouseButtons: Set<number> = new Set();
    private _currentMouseButtons: Set<number> = new Set();
    private _mouseWheelDelta: number = 0;

    public get state(): InputState {
        return this._state;
    }

    /**
     * Check if a key was just pressed this frame
     */
    public wasKeyJustPressed(key: string): boolean {
        return this._currentKeys.has(key) && !this._previousKeys.has(key);
    }

    /**
     * Check if a key was just released this frame
     */
    public wasKeyJustReleased(key: string): boolean {
        return !this._currentKeys.has(key) && this._previousKeys.has(key);
    }

    /**
     * Check if a mouse button was just pressed this frame
     */
    public wasMouseButtonJustPressed(button: number): boolean {
        return this._currentMouseButtons.has(button) && !this._previousMouseButtons.has(button);
    }

    /**
     * Check if a mouse button was just released this frame
     */
    public wasMouseButtonJustReleased(button: number): boolean {
        return !this._currentMouseButtons.has(button) && this._previousMouseButtons.has(button);
    }

    constructor(canvas: HTMLCanvasElement) {
        this._canvas = canvas;
        this._state = {
            keyboard: new Map(),
            mouse: {
                x: 0,
                y: 0,
                deltaX: 0,
                deltaY: 0,
                buttons: new Map(),
                wheel: 0,
            },
            pointerLocked: false,
        };

        this._setupEventListeners();
    }

    /**
     * Check if a key was just pressed this frame
     */
    public isKeyPressed(key: string): boolean {
        return this._state.keyboard.get(key.toLowerCase()) === "pressed";
    }

    /**
     * Check if a key is currently held down
     */
    public isKeyHeld(key: string): boolean {
        const state = this._state.keyboard.get(key.toLowerCase());
        return state === "pressed" || state === "held";
    }

    /**
     * Check if a key was just released this frame
     */
    public isKeyReleased(key: string): boolean {
        return this._state.keyboard.get(key.toLowerCase()) === "released";
    }

    /**
     * Check if a mouse button was just pressed
     */
    public isMouseButtonPressed(button: number): boolean {
        return this._state.mouse.buttons.get(button) === "pressed";
    }

    /**
     * Check if a mouse button is held
     */
    public isMouseButtonHeld(button: number): boolean {
        const state = this._state.mouse.buttons.get(button);
        return state === "pressed" || state === "held";
    }

    /**
     * Request pointer lock for FPS-style controls
     */
    public requestPointerLock(): void {
        this._canvas.requestPointerLock();
    }

    /**
     * Exit pointer lock
     */
    public exitPointerLock(): void {
        document.exitPointerLock();
    }

    /**
     * Update input state - call once per frame
     */
    public update(): void {
        // Update keyboard states
        for (const [key, state] of this._state.keyboard) {
            if (state === "pressed") {
                this._state.keyboard.set(key, "held");
            } else if (state === "released") {
                this._state.keyboard.set(key, "up");
            }
        }

        // Update mouse button states
        for (const [button, state] of this._state.mouse.buttons) {
            if (state === "pressed") {
                this._state.mouse.buttons.set(button, "held");
            } else if (state === "released") {
                this._state.mouse.buttons.set(button, "up");
            }
        }

        // Reset mouse delta and wheel
        this._state.mouse.deltaX = 0;
        this._state.mouse.deltaY = 0;
        this._state.mouse.wheel = this._mouseWheelDelta;
        this._mouseWheelDelta = 0;
    }

    /**
     * Dispose and remove event listeners
     */
    public dispose(): void {
        document.removeEventListener("keydown", this._onKeyDown);
        document.removeEventListener("keyup", this._onKeyUp);
        this._canvas.removeEventListener("mousedown", this._onMouseDown);
        this._canvas.removeEventListener("mouseup", this._onMouseUp);
        this._canvas.removeEventListener("mousemove", this._onMouseMove);
        this._canvas.removeEventListener("wheel", this._onWheel);
        document.removeEventListener("pointerlockchange", this._onPointerLockChange);
    }

    private _setupEventListeners(): void {
        document.addEventListener("keydown", this._onKeyDown);
        document.addEventListener("keyup", this._onKeyUp);
        this._canvas.addEventListener("mousedown", this._onMouseDown);
        this._canvas.addEventListener("mouseup", this._onMouseUp);
        this._canvas.addEventListener("mousemove", this._onMouseMove);
        this._canvas.addEventListener("wheel", this._onWheel, { passive: false });
        document.addEventListener("pointerlockchange", this._onPointerLockChange);

        // Prevent context menu on right-click
        this._canvas.addEventListener("contextmenu", (e) => e.preventDefault());
    }

    private _onKeyDown = (event: KeyboardEvent): void => {
        const key = event.key.toLowerCase();
        if (!this._currentKeys.has(key)) {
            this._state.keyboard.set(key, "pressed");
            this._currentKeys.add(key);
        }
    };

    private _onKeyUp = (event: KeyboardEvent): void => {
        const key = event.key.toLowerCase();
        this._state.keyboard.set(key, "released");
        this._currentKeys.delete(key);
    };

    private _onMouseDown = (event: MouseEvent): void => {
        if (!this._currentMouseButtons.has(event.button)) {
            this._state.mouse.buttons.set(event.button, "pressed");
            this._currentMouseButtons.add(event.button);
        }
    };

    private _onMouseUp = (event: MouseEvent): void => {
        this._state.mouse.buttons.set(event.button, "released");
        this._currentMouseButtons.delete(event.button);
    };

    private _onMouseMove = (event: MouseEvent): void => {
        this._state.mouse.x = event.clientX;
        this._state.mouse.y = event.clientY;
        this._state.mouse.deltaX += event.movementX;
        this._state.mouse.deltaY += event.movementY;
    };

    private _onWheel = (event: WheelEvent): void => {
        event.preventDefault();
        this._mouseWheelDelta += event.deltaY;
    };

    private _onPointerLockChange = (): void => {
        this._state.pointerLocked = document.pointerLockElement === this._canvas;
    };
}
